import { useEffect, useState } from 'react';
import { Camera, FileText, History, ImagePlus, MapPin, MapPinOff, ShieldCheck } from 'lucide-react';
import type { SubdivisionStockRow, Unit, WriteOffLocation } from '@eombor/contracts';
import { Button, Dialog, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { PhotoGrid } from '../../handover/ui/PhotoGrid.js';
import { PhotoPicker } from '../../handover/ui/PhotoPicker.js';
import { LotUsageList } from '../../storekeeping/ui/LotUsageList.js';
import { captureLocation } from '../model/geolocation.js';
import { useCreateWriteOff } from '../model/write-off.queries.js';

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/**
 * Xato matni — SERVERNIKI, bor bo'lsa.
 *
 * Ilgari bu yerda bitta qat'iy jumla turardi: "Aktni yuborib bo'lmadi.
 * Qaytadan urinib ko'ring." U ikki marta yomon edi. Birinchidan, maslahati
 * NOTO'G'RI: aksariyat xatolar (ruxsat yo'q, qoldiq yetmaydi, sabab
 * bo'sh) qayta urinishdan tuzalmaydi va odam bir xil natijani qayta-qayta
 * oladi. Ikkinchidan, u serverning aniq javobini — "bu amal sizning
 * rolingizga berilmagan" kabi — butunlay yo'q qilardi, ya'ni xodim ham,
 * uni qidirgan dasturchi ham sababdan mahrum bo'lardi.
 */
const RETRY = 'Aktni yuborib bo\'lmadi. Qaytadan urinib ko\'ring.';

const errorText = (error: unknown): string =>
  error instanceof ApiClientError ? error.message : RETRY;

interface PhotoDraft {
  id: string;
  file: File;
  url: string;
}

/**
 * RASXOD OYNASI — bitta satrni hisobdan chiqarishga yuborish.
 *
 * Uch narsa MAJBURIY va uchalasi ham ataylab: akt qog'ozi (buxgalteriya
 * uchun hujjat), rasm (ishlatilganining dalili) va tasdiq belgilari
 * (xodim javobgarlikni ongli ravishda oladi). Lokatsiya esa majburiy
 * EMAS — telefonda geolokatsiya o'chiq bo'lsa, xodim umuman rasxod qila
 * olmay qolardi.
 *
 * Yuqori chegara — `qty - pendingWriteOffQty`: qoldiqning o'zi emas.
 * Aks holda 3 donadan uchta alohida akt yuborib, 9 dona rasxod qilib
 * bo'lardi.
 */
export const WriteOffDialog = ({
  open,
  row,
  onClose,
}: {
  open: boolean;
  row: SubdivisionStockRow;
  onClose: () => void;
}) => {
  const available = Math.max(0, Math.round((row.qty - row.pendingWriteOffQty) * 1000) / 1000);

  /* Mobil ilovada ishlatilgan, lekin hali rasxod qilinmagan miqdor —
     akt aynan shu uchun yoziladi, shuning uchun u oldindan qo'yiladi. */
  const usedQty = Math.max(0, Math.round((row.qty - row.shelfQty - row.pendingWriteOffQty) * 1000) / 1000);
  const [qty, setQty] = useState(usedQty > 0 ? qtyText(Math.min(usedQty, available)) : '1');
  const [reason, setReason] = useState('');
  const [actFile, setActFile] = useState<File | null>(null);
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [used, setUsed] = useState(false);
  const [truthful, setTruthful] = useState(false);
  const [location, setLocation] = useState<WriteOffLocation | null>(null);
  const [locating, setLocating] = useState(true);

  const create = useCreateWriteOff();

  /*
   * Lokatsiya oyna OCHILGANDA so'raladi, yuborishda emas.
   *
   * Brauzer ruxsat so'rovini tugma bosilgandan keyin chiqarsa, u
   * yuborishni sekundlarga kechiktirardi va odam tugmani ikkinchi marta
   * bosardi.
   */
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLocating(true);
    void captureLocation().then((found) => {
      if (!alive) return;
      setLocation(found);
      setLocating(false);
    });
    return () => {
      alive = false;
    };
  }, [open]);

  /* Ko'rib chiqish havolalari BO'SHATILADI: oyna bir necha marta
     ochilsa, ular xotirada to'planib qolardi. */
  useEffect(
    () => () => {
      for (const photo of photos) URL.revokeObjectURL(photo.url);
    },
    [photos],
  );

  const addPhotos = (files: File[]): void => {
    setPhotos((current) => [
      ...current,
      ...files.map((file) => ({
        id: `${file.name}-${file.size}-${String(Math.random()).slice(2, 8)}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePhoto = (id: string): void => {
    setPhotos((current) => {
      const found = current.find((photo) => photo.id === id);
      if (found !== undefined) URL.revokeObjectURL(found.url);
      return current.filter((photo) => photo.id !== id);
    });
  };

  const parsedQty = Number(qty.replace(',', '.'));
  const qtyValid = Number.isFinite(parsedQty) && parsedQty > 0 && parsedQty <= available;

  /*
   * RASM — bu mahsulot haqiqatan ishlatilganining dalili. Ilovada
   * skanerlab olingan miqdor uchun dalil ALLAQACHON bor va u rasmdan
   * kuchliroq: tizimda kim olgani, qachon olgani va o'z so'zlari bilan
   * nima uchun olgani yozilib qolgan.
   *
   * Shuning uchun shart miqdorga bog'liq: yozib qo'yilganidan ko'proq
   * chiqarilayotgan bo'lsa, ortig'ining dalili yo'q va rasm qaytadan
   * majburiy bo'ladi. Server ham AYNAN shu hisobni qiladi
   * (`writeoff/domain/availability.ts`) — ekran serverdan yumshoq
   * bo'lsa, odam to'ldirib bo'lgandan keyin 400 olardi.
   */
  const photoRequired = !(qtyValid && usedQty > 0 && parsedQty <= usedQty);

  const ready =
    qtyValid &&
    reason.trim().length > 0 &&
    actFile !== null &&
    (photos.length > 0 || !photoRequired) &&
    used &&
    truthful;

  const submit = (): void => {
    if (!ready || actFile === null) return;
    create.mutate(
      {
        lotId: row.lotId,
        qty: parsedQty,
        reason: reason.trim(),
        location,
        actFile,
        photos: photos.map((photo) => photo.file),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('writeOff.dialogTitle')}
      description={t('writeOff.dialogSubtitle', {
        name: row.nomenclatureName,
        qty: qtyText(available),
        unit: unitText(row.unit as Unit),
      })}
      busy={create.isPending}
      error={create.isError ? errorText(create.error) : undefined}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            {t('writeOff.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} loading={create.isPending}>
            {t('writeOff.submit')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('writeOff.qty')}</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={available}
            step="any"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
            className={cn(
              'w-40 rounded-[var(--radius-sm)] border border-border-control bg-surface',
              'px-3 py-2 text-base text-text hover:border-border-strong',
            )}
          />
          {/* Chegara SABABI ko'rsatiladi: "3 dona bor edi, nega yubora
              olmayapman" degan savol aynan shu yerda tug'iladi. */}
          {row.pendingWriteOffQty > 0 ? (
            <span className="text-xs text-text-muted">
              {t('writeOff.pendingHint', {
                qty: qtyText(row.qty),
                unit: unitText(row.unit as Unit),
                pending: qtyText(row.pendingWriteOffQty),
              })}
            </span>
          ) : null}
          {usedQty > 0 ? (
            <span className="text-xs text-warn">{t('myStock.usedNotWrittenOff', { qty: qtyText(usedQty) })}</span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('writeOff.reason')}</span>
          <textarea
            rows={2}
            value={reason}
            maxLength={500}
            placeholder={t('writeOff.reasonPlaceholder')}
            onChange={(event) => setReason(event.target.value)}
            className={cn(
              'w-full resize-y rounded-[var(--radius-sm)] border border-border-control bg-surface',
              'px-3 py-2 text-base text-text placeholder:text-text-faint hover:border-border-strong',
            )}
          />
        </label>

        {/* AKT QOG'OZI — PDF yoki skan. `PhotoPicker` ishlatilmaydi: u
            faqat rasm qabul qiladi, akt esa ko'pincha PDF bo'ladi. */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('writeOff.actFile')}</span>
          <label
            className={cn(
              'inline-flex h-11 w-fit cursor-pointer items-center gap-2 rounded-[var(--radius-sm)]',
              'border border-border bg-surface-2 px-4 text-sm font-medium text-text',
              'hover:border-border-strong focus-within:border-accent',
            )}
          >
            <FileText size={16} aria-hidden />
            {actFile === null ? t('writeOff.attachAct') : t('writeOff.changeFile')}
            <input
              type="file"
              accept=".pdf,image/*"
              className="sr-only"
              onChange={(event) => setActFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <span className="text-xs text-text-muted">
            {actFile === null ? t('writeOff.noFile') : actFile.name}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text">
            {photoRequired ? t('writeOff.photoLabel') : t('writeOff.photoOptional')}
          </span>
          {/* Nega so'ralmayotgani AYTILADI: «majburiy» yozuvi yo'qolib
              qolsa, odam uni o'tkazib yuborilgan xato deb o'ylardi. */}
          {photoRequired ? null : (
            <p className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" aria-hidden />
              {t('writeOff.photoProven', { qty: qtyText(usedQty) })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <PhotoPicker
              label={t('writeOff.capture')}
              icon={<Camera size={16} aria-hidden />}
              capture="environment"
              tone="primary"
              onPick={addPhotos}
            />
            <PhotoPicker
              label={t('writeOff.fromGallery')}
              icon={<ImagePlus size={16} aria-hidden />}
              onPick={addPhotos}
            />
          </div>
          {/* `PhotoGrid` berish oqimidan qayta ishlatiladi — u yuklash
              holatini ham ko'rsatadi. Bu yerda fayllar akt bilan BIRGA
              yuboriladi, shuning uchun holat doim `ready`. */}
          <PhotoGrid
            photos={photos.map((photo) => ({
              id: photo.id,
              name: photo.file.name,
              previewUrl: photo.url,
              fileId: null,
              status: 'ready' as const,
            }))}
            onRemove={removePhoto}
          />
        </div>

        {/* LOKATSIYA — majburiy emas, lekin holati KO'RINADI: xodim
            "olindi/olinmadi" ni bilib turishi kerak. */}
        <p className="flex items-center gap-2 text-xs text-text-muted">
          {locating ? (
            <>
              <MapPin size={14} aria-hidden />
              {t('writeOff.locating')}
            </>
          ) : location === null ? (
            <>
              <MapPinOff size={14} aria-hidden />
              {t('writeOff.locationFailed')}
            </>
          ) : (
            <>
              <MapPin size={14} aria-hidden />
              {t('writeOff.locationOk')}
            </>
          )}
        </p>

        {/* KIM NIMAGA ISHLATGAN — akt aynan shu olishlar uchun yoziladi.
            Ro'yxat bo'sh bo'lsa (hech kim ilovada olmagan) umuman
            chizilmaydi: rasxodning boshqa sabablari ham bor. */}
        {usedQty > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1.5 text-sm font-medium text-text">
              <History size={14} aria-hidden />
              {t('writeOff.usageTitle')}
            </span>
            <LotUsageList lotId={row.lotId} unit={row.unit} hideWhenEmpty />
          </div>
        ) : null}

        {/* TASDIQ BELGILARI — javobgarlik ONGLI ravishda olinadi. */}
        <div className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-border bg-surface-2 p-3">
          <label className="flex items-start gap-2 text-sm text-text">
            <input
              type="checkbox"
              checked={used}
              onChange={(event) => setUsed(event.target.checked)}
              className="mt-0.5"
            />
            {t('writeOff.confirmUsed')}
          </label>
          <label className="flex items-start gap-2 text-sm text-text">
            <input
              type="checkbox"
              checked={truthful}
              onChange={(event) => setTruthful(event.target.checked)}
              className="mt-0.5"
            />
            {t('writeOff.confirmTruthful')}
          </label>
        </div>
      </div>
    </Dialog>
  );
};
